/**
 * Trades from a broker's own export, read into journal entries.
 *
 * One format so far: Tradovate's Performance report (Reports → Performance →
 * export), which is what most prop-firm futures accounts are traded on. Each of
 * its rows is a buy fill matched against a sell fill, so a position scaled in or
 * out of arrives as several rows that share a fill. Those are put back together
 * here: the journal records the trade that was taken, not how the platform
 * paired its fills.
 *
 * The result is what Tradovate reports as P&L, which is before commissions --
 * the report does not include them, and nothing here invents them.
 *
 * Shared by the browser, which reads the file and shows what it found, and the
 * server, whose tests read the same sample through the same code.
 */

import { readCsv } from './csv.js';

/* The columns that make a file a Tradovate Performance export. */
const TRADOVATE = ['symbol', 'buyfillid', 'sellfillid', 'qty', 'buyprice', 'sellprice', 'pnl', 'boughttimestamp', 'soldtimestamp'];

/* A futures contract code: the root, the month letter, the year -- MESZ6, 6EH27, M2KU6. */
const CONTRACT = /^(.+?)([FGHJKMNQUVXZ])(\d{1,2})$/;

/** The fill ids an import key was made of: 'tradovate:1-2,1-3' is 1, 2 and 3. */
export function importFills(key) {
  const body = String(key || '').split(':').slice(1).join(':');
  return [...new Set(body.split(',').flatMap(pair => pair.split('-')).map(id => id.trim()).filter(Boolean))];
}

/** Whether a note is still the line an import wrote, rather than something the trader typed over it. */
export const isImportNote = note => /^Tradovate /.test(String(note || ''));

/**
 * Imported trades that look like one typed in by hand: the same day, the same
 * instrument (MESZ6 and MES alike), the same side, and a result within a few
 * per cent -- a typed one often has the commission taken off. Each typed trade
 * is matched once, so two identical trades in the export do not both hide
 * behind one typed in.
 *
 * Returns the imported trades' keys, mapped to the typed trade they resemble.
 */
export function typedTwins(imported, journal) {
  const typed = journal.filter(row => !row.imported && !row.backtestId);
  const used = new Set();
  const twins = new Map();
  for (const trade of imported) {
    const near = typed.find(row => !used.has(row.id)
      && row.date === trade.date
      && contractRoot(row.pair) === contractRoot(trade.pair)
      && row.side === trade.side
      && Math.abs(Number(row.netPl) - trade.netPl) <= Math.max(1, Math.abs(trade.netPl) * 0.05));
    if (near) { used.add(near.id); twins.set(trade.importKey, near); }
  }
  return twins;
}

/** The instrument a contract is on: MESZ6 is MES. The contract itself stays in the note. */
export function contractRoot(symbol) {
  const code = String(symbol || '').trim().toUpperCase();
  const match = CONTRACT.exec(code);
  return match && match[1].length >= 1 ? match[1] : code;
}

/**
 * "$92.50", "$(78.75)", "-$1,234.50", "(12.00)" -- a number, or null.
 * Tradovate writes a loss the accountant's way, in brackets.
 */
export function tradovateMoney(value) {
  let text = String(value ?? '').trim();
  if (!text) return null;
  let negative = false;
  if (/^\(.*\)$/.test(text.replace(/[$\s]/g, ''))) negative = true;
  if (/^-/.test(text) || /^\$\s*-/.test(text)) negative = true;
  text = text.replace(/[$()\s,+-]/g, '');
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  const amount = Number(text);
  return negative ? -amount : amount;
}

/**
 * "10/05/2026 16:04:26" (or with AM/PM) -> { day: '2026-10-05', time: '16:04', stamp }.
 * Month first: that is how Tradovate writes dates whatever the account's country.
 */
export function tradovateTime(value) {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?)?$/.exec(String(value ?? '').trim());
  if (!match) return null;
  const [, mo, d, y, hh = '0', mm = '0', ss = '0', half] = match;
  let hour = Number(hh);
  if (half) {
    if (hour > 12) return null;
    hour = hour % 12 + (/p/i.test(half) ? 12 : 0);
  }
  if (Number(mo) < 1 || Number(mo) > 12 || Number(d) < 1 || Number(d) > 31 || hour > 23 || Number(mm) > 59) return null;
  const pad = n => String(n).padStart(2, '0');
  const day = `${y}-${pad(mo)}-${pad(d)}`;
  const time = `${pad(hour)}:${mm}`;
  return { day, time, stamp: `${day}T${time}:${pad(ss)}` };
}

/* A price written to the tick, as many decimals as the export used for it. */
const decimalsOf = text => (String(text).split('.')[1] || '').length;
const priceText = (n, decimals) => (Number.isFinite(n) ? n.toFixed(Math.min(decimals, 6)) : '');

/**
 * Reads a broker export.
 *
 * Returns `{ source, trades, skipped }` -- `source` null when the file is not a
 * format this knows -- where each trade is ready for POST /api/trades/import:
 * date, pair, side, lots, netPl, currency, note, and `importKey`, which is the
 * same for the same trade in every export, so importing a month twice adds it once.
 */
export function readBrokerTrades(text) {
  const { rows, lines } = readCsv(text, ',');
  const at = rows.findIndex(row => {
    const names = row.map(cell => cell.toLowerCase().replace(/[^a-z]/g, ''));
    return TRADOVATE.every(name => names.includes(name));
  });
  if (at < 0) return { source: null, trades: [], skipped: [] };

  const names = rows[at].map(cell => cell.toLowerCase().replace(/[^a-z]/g, ''));
  const col = name => names.indexOf(name);
  const cell = (row, name) => (col(name) >= 0 ? String(row[col(name)] ?? '').trim() : '');

  /* Every readable fill pair, with the line it came from for anything that is not. */
  const pairs = [];
  const skipped = [];
  for (let i = at + 1; i < rows.length; i += 1) {
    const row = rows[i];
    const symbol = cell(row, 'symbol').toUpperCase();
    const qty = Number(cell(row, 'qty'));
    const pnl = tradovateMoney(cell(row, 'pnl'));
    const bought = tradovateTime(cell(row, 'boughttimestamp'));
    const sold = tradovateTime(cell(row, 'soldtimestamp'));
    const buyFill = cell(row, 'buyfillid');
    const sellFill = cell(row, 'sellfillid');
    if (!symbol || !(qty > 0) || pnl === null || !bought || !sold || !buyFill || !sellFill) {
      skipped.push(lines[i] ?? i + 1);
      continue;
    }
    pairs.push({
      symbol, qty, pnl, bought, sold, buyFill, sellFill,
      buyPrice: Number(cell(row, 'buyprice')), sellPrice: Number(cell(row, 'sellprice')),
      decimals: Math.max(decimalsOf(cell(row, 'buyprice')), decimalsOf(cell(row, 'sellprice'))),
      duration: cell(row, 'duration')
    });
  }

  /*
   * Rows sharing a fill are one position: the same entry closed in parts, or
   * several entries closed at once. Grouped through the fills they share, per
   * contract, so a chain of partial fills ends up as the one trade it was.
   */
  const parent = pairs.map((_, i) => i);
  const find = i => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  const byFill = new Map();
  pairs.forEach((pair, i) => {
    for (const fill of [pair.symbol + ':' + pair.buyFill, pair.symbol + ':' + pair.sellFill]) {
      if (byFill.has(fill)) parent[find(i)] = find(byFill.get(fill));
      else byFill.set(fill, i);
    }
  });
  const groups = new Map();
  pairs.forEach((pair, i) => {
    const root = find(i);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root).push(pair);
  });

  const trades = [...groups.values()].map(group => {
    /* Bought before it was sold is a long; sold first is a short. */
    const long = group[0].bought.stamp <= group[0].sold.stamp;
    const entry = pair => (long ? pair.bought : pair.sold);
    const exit = pair => (long ? pair.sold : pair.bought);
    const lots = group.reduce((sum, pair) => sum + pair.qty, 0);
    const netPl = Math.round(group.reduce((sum, pair) => sum + pair.pnl, 0) * 100) / 100;
    const weighted = key => group.reduce((sum, pair) => sum + pair[key] * pair.qty, 0) / lots;
    const opened = group.map(entry).sort((a, b) => a.stamp.localeCompare(b.stamp))[0];
    const closed = group.map(exit).sort((a, b) => b.stamp.localeCompare(a.stamp))[0];
    const [inPrice, outPrice] = long ? [weighted('buyPrice'), weighted('sellPrice')] : [weighted('sellPrice'), weighted('buyPrice')];
    const decimals = Math.max(...group.map(pair => pair.decimals));
    const contracts = [...new Set(group.map(pair => pair.symbol))].join(', ');
    const fills = [...new Set(group.map(pair => pair.buyFill + '-' + pair.sellFill))].sort();
    const span = opened.day === closed.day ? `${opened.time}–${closed.time}` : `${opened.day} ${opened.time} – ${closed.day} ${closed.time}`;
    return {
      date: opened.day,
      pair: contractRoot(group[0].symbol),
      side: long ? 'LONG' : 'SHORT',
      lots,
      netPl,
      /* Futures on Tradovate settle in dollars. */
      currency: 'USD',
      note: [
        `Tradovate ${contracts}`,
        `${priceText(inPrice, decimals)} → ${priceText(outPrice, decimals)}`,
        span + (group.length === 1 && group[0].duration ? ` (${group[0].duration})` : '')
      ].join(' · ').slice(0, 400),
      importKey: ('tradovate:' + fills.join(',')).slice(0, 300),
      openedAt: opened.stamp
    };
  }).sort((a, b) => a.openedAt.localeCompare(b.openedAt));

  return { source: 'tradovate', trades, skipped };
}
